import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { MapPin, Star, Trash2, CalendarDays, Zap, RefreshCw } from 'lucide-react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import useVisits from './useVisits';
import useStrava from './useStrava';
import LogVisitModal from './LogVisitModal';
import StravaSyncModal from './StravaSyncModal';

const toResortSlug = (name, id) => {
  const slug = name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}-${id.substring(0, 8)}`;
};

const Profile = () => {
  const { user, loading: authLoading, signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [visitedResorts, setVisitedResorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { visits, addVisit, removeVisit, getImportedStravaIds } = useVisits();
  const [showLogVisit, setShowLogVisit] = useState(false);
  const [resortList, setResortList] = useState([]);
  const strava = useStrava();
  const [showStravaSync, setShowStravaSync] = useState(false);
  const [stravaSyncResult, setStravaSyncResult] = useState(null);
  const [importedStravaIds, setImportedStravaIds] = useState(new Set());
  const [stravaBanner, setStravaBanner] = useState(null);
  const [stravaError, setStravaError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('strava');
    if (status === 'connected') {
      setStravaBanner('Strava connected. Click "Sync activities" to import your ski days.');
    } else if (status === 'error') {
      setStravaBanner(null);
      setStravaError(`Strava connection failed: ${params.get('reason') || 'unknown'}`);
    }
    if (status) {
      params.delete('strava');
      params.delete('reason');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchVisited = async () => {
      try {
        const q = query(
          collection(db, 'users', user.uid, 'visitedResorts'),
          orderBy('visitedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        setVisitedResorts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching visited resorts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVisited();
  }, [user]);

  // Fetch resort list for the Log Visit modal search
  useEffect(() => {
    if (!user) return;
    fetch('/api/ski_areas')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.features) {
          setResortList(data.features.map(f => ({
            id: f.properties?.id,
            name: f.properties?.name,
            country: f.properties?.country,
          })).filter(r => r.id && r.name));
        }
      })
      .catch(() => {});
  }, [user]);

  if (authLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5" style={{ maxWidth: '500px' }}>
        <Card className="text-center p-4" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
          <Card.Body>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--pm-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-3">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            <h5 style={{ color: 'var(--pm-text)' }}>Sign in to track your resorts</h5>
            <p style={{ color: 'var(--pm-text-muted)', fontSize: '0.9rem' }}>
              Keep track of where you've been and rate your favorite ski resorts.
            </p>
            <Button variant="primary" onClick={signInWithGoogle}>
              Sign In with Google
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const ratedResorts = visitedResorts.filter(r => r.rating != null);
  const avgRating = ratedResorts.length > 0
    ? (ratedResorts.reduce((sum, r) => sum + r.rating, 0) / ratedResorts.length).toFixed(1)
    : '-';

  const handleConnectStrava = async () => {
    setStravaError(null);
    try {
      await strava.connectStrava();
    } catch (err) {
      setStravaError(err.message || 'Failed to start Strava connection');
    }
  };

  const handleSyncStrava = async () => {
    setStravaError(null);
    try {
      const imported = await getImportedStravaIds();
      setImportedStravaIds(imported);
      setShowStravaSync(true);
      setStravaSyncResult(null);
      const data = await strava.syncActivities();
      setStravaSyncResult(data);
    } catch (err) {
      setStravaError(err.message || 'Strava sync failed');
      setShowStravaSync(false);
    }
  };

  return (
    <Container className="py-4" style={{ maxWidth: '900px' }}>
      {/* Profile header */}
      <Card className="mb-4" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
        <Card.Body className="d-flex align-items-center gap-3">
          {user.photoURL ? (
            <img src={user.photoURL} alt="" className="user-avatar" referrerPolicy="no-referrer" style={{ width: '56px', height: '56px' }} />
          ) : (
            <div className="user-avatar user-avatar-fallback" style={{ width: '56px', height: '56px', fontSize: '1.4rem' }}>
              {(user.displayName || user.email || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <h5 className="mb-0" style={{ color: 'var(--pm-text)' }}>{user.displayName}</h5>
            <small style={{ color: 'var(--pm-text-muted)' }}>{user.email}</small>
          </div>
        </Card.Body>
      </Card>

      {/* Strava banners */}
      {stravaBanner && (
        <Alert variant="success" dismissible onClose={() => setStravaBanner(null)} className="py-2" style={{ fontSize: '0.85rem' }}>
          {stravaBanner}
        </Alert>
      )}
      {stravaError && (
        <Alert variant="danger" dismissible onClose={() => setStravaError(null)} className="py-2" style={{ fontSize: '0.85rem' }}>
          {stravaError}
        </Alert>
      )}

      {/* Strava integration */}
      <Card className="mb-4" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
        <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <Zap size={20} style={{ color: '#fc4c02' }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--pm-text)' }}>Strava</div>
              <small style={{ color: 'var(--pm-text-muted)' }}>
                {strava.loading
                  ? 'Checking status...'
                  : strava.connected
                    ? (strava.lastSyncedAt
                        ? `Connected · last synced ${strava.lastSyncedAt.toLocaleDateString()}`
                        : 'Connected · no sync yet')
                    : 'Import past ski activities as visits'}
              </small>
            </div>
          </div>
          <div className="d-flex gap-2">
            {strava.connected ? (
              <Button variant="primary" size="sm" onClick={handleSyncStrava} disabled={strava.syncing}>
                {strava.syncing ? <Spinner animation="border" size="sm" /> : <><RefreshCw size={14} className="me-1" />Sync activities</>}
              </Button>
            ) : (
              <Button variant="outline-primary" size="sm" onClick={handleConnectStrava} disabled={strava.loading}>
                Connect Strava
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Stats */}
      <Row className="g-3 mb-4">
        <Col xs={3}>
          <Card className="text-center p-3" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--pm-accent)' }}>{visits.length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)' }}>Ski Days</div>
          </Card>
        </Col>
        <Col xs={3}>
          <Card className="text-center p-3" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--pm-accent)' }}>{visitedResorts.length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)' }}>Resorts</div>
          </Card>
        </Col>
        <Col xs={3}>
          <Card className="text-center p-3" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--pm-accent)' }}>{ratedResorts.length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)' }}>Rated</div>
          </Card>
        </Col>
        <Col xs={3}>
          <Card className="text-center p-3" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--pm-accent)' }}>{avgRating}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)' }}>Avg Rating</div>
          </Card>
        </Col>
      </Row>

      {/* Resort list */}
      <Card style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
        <Card.Body>
          <h6 className="mb-3" style={{ color: 'var(--pm-text)' }}>My Resorts</h6>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" variant="primary" /></div>
          ) : visitedResorts.length === 0 ? (
            <div className="text-center py-4">
              <p style={{ color: 'var(--pm-text-muted)', margin: 0 }}>
                You haven't visited any resorts yet. Explore the <Link to="/">map</Link> to find resorts!
              </p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {visitedResorts.map((resort) => (
                <Link
                  key={resort.id}
                  to={`/resort/${toResortSlug(resort.resortName, resort.resortId)}`}
                  className="text-decoration-none"
                >
                  <div className="profile-resort-item">
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--pm-text)' }}>
                        {resort.resortName}
                      </div>
                      {resort.visitedAt && (
                        <small style={{ color: 'var(--pm-text-muted)' }}>
                          <MapPin size={12} style={{ marginRight: '4px' }} />
                          {resort.visitedAt.toDate ? resort.visitedAt.toDate().toLocaleDateString() : new Date(resort.visitedAt).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                    {resort.rating != null && (
                      <div className="profile-resort-rating">
                        {resort.rating}<span style={{ fontSize: '0.7rem', color: 'var(--pm-text-muted)' }}>/10</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      {/* Visits */}
      <Card className="mt-4" style={{ background: 'var(--pm-bg-surface)', border: '1px solid var(--pm-border)' }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0" style={{ color: 'var(--pm-text)' }}>Ski Visits</h6>
            <Button variant="outline-primary" size="sm" onClick={() => setShowLogVisit(true)} style={{ borderRadius: '20px', padding: '4px 14px' }}>
              <CalendarDays size={14} className="me-1" />
              Log Visit
            </Button>
          </div>
          {visits.length === 0 ? (
            <div className="text-center py-4">
              <p style={{ color: 'var(--pm-text-muted)', margin: 0 }}>
                No visits logged yet. Log your first ski day!
              </p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {visits.map(visit => (
                <div key={visit.id} className="visit-list-item">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Link
                        to={`/resort/${toResortSlug(visit.resortName, visit.resortId)}`}
                        className="text-decoration-none"
                        style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--pm-text)' }}
                      >
                        {visit.resortName}
                      </Link>
                      <span className="visit-list-stars">
                        {[1,2,3,4,5].map(n => (
                          <Star key={n} size={12} fill={visit.snowRating >= n ? 'var(--pm-accent)' : 'none'} stroke={visit.snowRating >= n ? 'var(--pm-accent)' : 'var(--pm-text-muted)'} />
                        ))}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <small style={{ color: 'var(--pm-text-muted)' }}>
                        {visit.date?.toDate ? visit.date.toDate().toLocaleDateString() : new Date(visit.date).toLocaleDateString()}
                      </small>
                      {visit.conditions?.length > 0 && (
                        <div className="d-flex gap-1 flex-wrap">
                          {visit.conditions.map(c => (
                            <span key={c} className="visit-condition-tag">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {visit.notes && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--pm-text-muted)', marginTop: '4px' }}>{visit.notes}</div>
                    )}
                  </div>
                  <button
                    className="visit-star-btn"
                    onClick={() => removeVisit(visit.id)}
                    title="Delete visit"
                  >
                    <Trash2 size={14} style={{ color: 'var(--pm-text-muted)' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Log Visit Modal */}
      <LogVisitModal
        show={showLogVisit}
        onHide={() => setShowLogVisit(false)}
        onSave={addVisit}
        resorts={resortList}
      />

      {/* Strava Sync Modal */}
      <StravaSyncModal
        show={showStravaSync}
        onHide={() => setShowStravaSync(false)}
        syncResult={stravaSyncResult}
        addVisit={addVisit}
        importedStravaIds={importedStravaIds}
        resorts={resortList}
      />
    </Container>
  );
};

export default Profile;
